/**
 * Collapsible docx↔schema tag audit panel for the schema editor. Runs
 * auditSchemaTags against the template's extracted body tags and the
 * (possibly unsaved) local schema: emerald all-clear when every derived
 * '*_enabled' tag matches a balanced pair in the document, amber expandable
 * detail otherwise (missing / unknown / unbalanced). Renders nothing when the
 * template has no extracted body tags (e.g. no version uploaded yet).
 * Styled on UnusedTagsPanel.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { auditSchemaTags } from '../../utils/schemaTagAudit';
import type { OrgTemplateSchema } from '../../types/orgTemplate';

interface SchemaTagAuditPanelProps {
  bodyTags: string[];
  schema: OrgTemplateSchema;
}

export default function SchemaTagAuditPanel({ bodyTags, schema }: SchemaTagAuditPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (bodyTags.length === 0) return null;

  const { missing, unknown, unbalanced } = auditSchemaTags(bodyTags, schema);
  const totalIssues = missing.length + unknown.length + unbalanced.length;

  if (totalIssues === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4">
        <p className="text-body-sm text-emerald-700 dark:text-emerald-400">
          All schema tags match the uploaded document.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between p-4 text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-body-sm font-medium text-amber-800 dark:text-amber-300">
            Document Tag Audit ({totalIssues})
          </span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-amber-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-amber-500" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-amber-200 dark:border-amber-800 p-4 space-y-4">
          {missing.length > 0 && (
            <div>
              <h4 className="text-body-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                Missing from Document ({missing.length})
              </h4>
              <p className="text-caption text-amber-600 dark:text-amber-400 mb-2">
                The schema derives these conditional tags, but the uploaded document does not
                contain them — those sections will not toggle.
              </p>
              <div className="space-y-1">
                {missing.map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center rounded-md bg-white dark:bg-slate-700 p-2 border border-amber-100 dark:border-amber-900/30"
                  >
                    <code className="text-body-sm text-slate-700 dark:text-slate-300">{tag}</code>
                  </div>
                ))}
              </div>
            </div>
          )}

          {unknown.length > 0 && (
            <div>
              <h4 className="text-body-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                Not in Schema ({unknown.length})
              </h4>
              <p className="text-caption text-amber-600 dark:text-amber-400 mb-2">
                These conditional tags exist in the document, but no module, order form or
                schedule derives them (derived tags never contain hyphens).
              </p>
              <div className="space-y-1">
                {unknown.map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center rounded-md bg-white dark:bg-slate-700 p-2 border border-amber-100 dark:border-amber-900/30"
                  >
                    <code className="text-body-sm text-slate-700 dark:text-slate-300">{tag}</code>
                  </div>
                ))}
              </div>
            </div>
          )}

          {unbalanced.length > 0 && (
            <div>
              <h4 className="text-body-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                Unbalanced Pairs ({unbalanced.length})
              </h4>
              <p className="text-caption text-amber-600 dark:text-amber-400 mb-2">
                Every {'{#tag}'} (or {'{^tag}'}) needs a matching {'{/tag}'} — generation fails on
                a broken pair.
              </p>
              <div className="space-y-1">
                {unbalanced.map(({ tag, problem }) => (
                  <div
                    key={tag}
                    className="flex items-center justify-between gap-3 rounded-md bg-white dark:bg-slate-700 p-2 border border-amber-100 dark:border-amber-900/30"
                  >
                    <code className="text-body-sm text-slate-700 dark:text-slate-300 break-all">
                      {tag}
                    </code>
                    <span className="text-caption text-amber-700 dark:text-amber-400 shrink-0">
                      {problem === 'missing-close' ? 'opened but never closed' : 'closed but never opened'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
